import type { HTMLAttributes, ReactNode } from 'react';
import { CircleCheck, Info, OctagonAlert, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Un aviso dentro de una pantalla (mapa de diseño 226, §3): un ícono, el título
 * en el tono y el texto en el color normal, nunca todo en ámbar oscuro.
 * Reemplaza los recuadros ámbar hechos a mano (`border-amber-…` `bg-amber-…`) y los
 * azules sueltos.
 */
const TONOS = {
  alerta: {
    caja: 'border-alerta-borde bg-alerta-suave',
    tinta: 'text-alerta',
    Icono: TriangleAlert,
  },
  info: { caja: 'border-info-borde bg-info-suave', tinta: 'text-info', Icono: Info },
  // «Nada que decidir en este producto» (spec 227 §4).
  ok: { caja: 'border-ok-borde bg-ok-suave', tinta: 'text-ok', Icono: CircleCheck },
  bloqueo: {
    caja: 'border-bloqueo-borde bg-bloqueo-suave',
    tinta: 'text-bloqueo',
    Icono: OctagonAlert,
  },
} as const;

export type TonoDeAviso = keyof typeof TONOS;

export function Aviso({
  tono = 'alerta',
  titulo,
  children,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, 'title'> & { tono?: TonoDeAviso; titulo?: ReactNode }) {
  const { caja, tinta, Icono } = TONOS[tono];
  return (
    <div
      data-tono={tono}
      className={cn('flex gap-2 rounded-md border p-3 text-xs text-foreground', caja, className)}
      {...props}
    >
      <Icono aria-hidden="true" className={cn('mt-px h-4 w-4 shrink-0', tinta)} />
      <div className="min-w-0 flex-1 space-y-0.5">
        {titulo && <p className={cn('font-semibold', tinta)}>{titulo}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
}
